import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Plus, Pencil, Trash2, ShieldCheck, Eye, EyeOff, X, Check, Activity, Home, Quote, ClipboardList, Wand2, Save } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const CONFIDENCE_TONES = {
  verified: "emerald",
  single_source: "cyan",
  rumor: "amber",
};
const CONFIDENCE_LABELS = {
  verified: "Verified",
  single_source: "Single Source",
  rumor: "Rumor",
};

const CATEGORY_META = {
  on_field: { icon: Activity, label: "On-Field", color: "#22D3EE" },
  off_field: { icon: Home, label: "Off-Field", color: "#60A5FA" },
};

const EMPTY_INTEL = {
  category: "on_field",
  topic: "",
  content_md: "",
  direct_quote: "",
  quote_attribution: "",
  source_name: "",
  source_role: "",
  confidence: "single_source",
  source_date: new Date().toISOString().slice(0, 10),
  tags: "",
  visible_to_family: false,
};

export function Player360Section({ playerId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [intel, setIntel] = useState([]);
  const [synth, setSynth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [synthesizing, setSynthesizing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const load = async () => {
    if (!playerId) return;
    setLoading(true);
    try {
      const [iRes, sRes] = await Promise.all([
        api.get(`/players/${playerId}/intel`),
        api.get(`/players/${playerId}/player360`),
      ]);
      setIntel(iRes.data);
      setSynth(sRes.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [playerId]);

  const grouped = useMemo(() => {
    const out = { on_field: [], off_field: [] };
    for (const i of intel) {
      if (out[i.category]) out[i.category].push(i);
    }
    return out;
  }, [intel]);

  const startCreate = (category) => {
    setEditing({ ...EMPTY_INTEL, category });
    setEditOpen(true);
  };
  const startEdit = (item) => {
    setEditing({ ...item, tags: (item.tags || []).join(", ") });
    setEditOpen(true);
  };
  const saved = async () => {
    setEditOpen(false);
    setEditing(null);
    await load();
  };
  const remove = async (item) => {
    if (!window.confirm("Delete this intel entry? This cannot be undone.")) return;
    await api.delete(`/intel/${item.id}`);
    toast.success("Intel deleted");
    await load();
  };

  const synthesize = async () => {
    setSynthesizing(true);
    try {
      const r = await api.post(`/players/${playerId}/player360/synthesize`);
      setSynth((s) => ({ ...(s || {}), draft: r.data }));
      toast.success("AI synthesis drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synthesis failed");
    } finally {
      setSynthesizing(false);
    }
  };
  const approve = async () => {
    const r = await api.post(`/players/${playerId}/player360/approve`);
    setSynth({ ...synth, ...r.data, draft: null });
    toast.success("Synthesis approved");
  };
  const reject = async () => {
    await api.post(`/players/${playerId}/player360/reject`);
    setSynth({ ...(synth || {}), draft: null });
    toast.success("Draft discarded");
  };
  const toggleVisibility = async () => {
    const next = !(synth?.visible_to_family);
    const r = await api.patch(`/players/${playerId}/player360/visibility`, null, { params: { visible_to_family: next } });
    setSynth({ ...synth, visible_to_family: r.data.visible_to_family });
    toast.success(next ? "Synthesis shared with player/family" : "Synthesis hidden from player/family");
  };

  // Non-admin: only show approved + visible synthesis, no intel editing.
  if (!isAdmin) {
    if (loading) return null;
    if (!synth?.synthesis_md) return null;
    return (
      <GlassCard className="col-span-12 p-6" testId="player360-readonly">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-cyan-300" />
          <h3 className="text-1 font-semibold">Player 360°</h3>
          <span className="ml-auto text-3 text-[11px]">Approved {synth.approved_at ? new Date(synth.approved_at).toLocaleDateString() : ""}</span>
        </div>
        <div className="markdown-body" data-testid="player360-synthesis-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{synth.synthesis_md}</ReactMarkdown>
        </div>
      </GlassCard>
    );
  }

  // Admin view
  return (
    <GlassCard className="col-span-12 p-6" testId="player360-admin">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Player 360°</h3>
            <StatusPill tone="neutral">Admin</StatusPill>
          </div>
          <p className="text-3 text-xs mt-1">Source-attributed intel across on-field and off-field — plus AI synthesis for the full-picture view.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="player360-parse-notes" onClick={() => setNotesOpen(true)} variant="outline" className="border-cyan-300/30 bg-cyan-300/5 text-cyan-100 hover:bg-cyan-300/10">
            <ClipboardList className="h-4 w-4" /> Parse Interview Notes
          </Button>
          <Button data-testid="player360-synthesize" onClick={synthesize} disabled={synthesizing} className="cta-primary">
            <Sparkles className="h-4 w-4" /> {synthesizing ? "Synthesizing…" : (synth?.synthesis_md ? "Regenerate Synthesis" : "Generate Synthesis")}
          </Button>
        </div>
      </div>

      {/* AI synthesis block */}
      <div className="grid grid-cols-1 gap-4 mb-5">
        {synth?.draft?.content_md ? (
          <div className="rounded-xl border border-cyan-300/30 bg-cyan-300/5 p-5" data-testid="player360-draft">
            <div className="flex items-center gap-2 mb-3">
              <StatusPill tone="amber">Pending Review</StatusPill>
              <span className="text-3 text-[11px]">Drafted {new Date(synth.draft.generated_at).toLocaleString()}</span>
              <div className="ml-auto flex gap-2">
                <Button data-testid="player360-approve" size="sm" onClick={approve} className="cta-primary"><Check className="h-3.5 w-3.5" /> Approve</Button>
                <Button data-testid="player360-reject" size="sm" variant="ghost" onClick={reject} className="text-rose-300"><X className="h-3.5 w-3.5" /> Discard</Button>
              </div>
            </div>
            <div className="markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{synth.draft.content_md}</ReactMarkdown>
            </div>
          </div>
        ) : null}

        {synth?.synthesis_md ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="player360-approved">
            <div className="flex items-center gap-2 mb-3">
              <StatusPill tone="emerald">Approved</StatusPill>
              <span className="text-3 text-[11px]">{synth.approved_at ? new Date(synth.approved_at).toLocaleString() : ""}</span>
              <button
                onClick={toggleVisibility}
                data-testid="player360-toggle-visibility"
                className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition ${synth.visible_to_family ? "bg-emerald-400/10 text-emerald-200 border-emerald-300/30" : "bg-white/5 text-white/70 border-white/10"}`}
              >
                {synth.visible_to_family ? <><Eye className="h-3 w-3" /> Visible to player/family</> : <><EyeOff className="h-3 w-3" /> Admin only</>}
              </button>
            </div>
            <div className="markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{synth.synthesis_md}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-white/10 p-5 text-center">
            <Sparkles className="h-5 w-5 text-cyan-300 mx-auto mb-1.5" />
            <div className="text-2 text-sm">No synthesis yet. Add intel below, then click <span className="text-1 font-semibold">Generate Synthesis</span>.</div>
          </div>
        )}
      </div>

      {/* Intel columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(["on_field", "off_field"]).map((cat) => {
          const meta = CATEGORY_META[cat];
          const Icon = meta.icon;
          const items = grouped[cat];
          return (
            <div key={cat} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`intel-column-${cat}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" style={{ color: meta.color }} />
                  <h4 className="text-1 font-semibold">{meta.label}</h4>
                  <StatusPill tone="neutral">{items.length}</StatusPill>
                </div>
                <Button data-testid={`intel-add-${cat}`} size="sm" variant="ghost" className="text-cyan-300" onClick={() => startCreate(cat)}>
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              {items.length === 0 ? (
                <div className="text-3 text-sm italic py-3">No {meta.label.toLowerCase()} intel yet.</div>
              ) : (
                <div className="space-y-2.5 max-h-[24rem] overflow-y-auto pr-1.5 scrollbar-thin" data-testid={`intel-scroll-${cat}`}>
                  {items.map((it) => (
                    <IntelRow key={it.id} item={it} onEdit={() => startEdit(it)} onDelete={() => remove(it)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editOpen && (
        <IntelEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          playerId={playerId}
          intel={editing}
          onSaved={saved}
        />
      )}

      {notesOpen && (
        <InterviewNotesDialog
          open={notesOpen}
          onOpenChange={setNotesOpen}
          playerId={playerId}
          onSaved={async () => { setNotesOpen(false); await load(); }}
        />
      )}
    </GlassCard>
  );
}

function IntelRow({ item, onEdit, onDelete }) {
  return (
    <div className="glass p-3 group" data-testid={`intel-row-${item.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-1 text-sm font-medium">{item.topic}</span>
            <StatusPill tone={CONFIDENCE_TONES[item.confidence] || "neutral"}>{CONFIDENCE_LABELS[item.confidence] || item.confidence}</StatusPill>
            {item.visible_to_family && <StatusPill tone="emerald">Shared</StatusPill>}
          </div>
          <div className="text-3 text-[11px] mt-0.5">
            {item.source_role ? `${item.source_role}` : ""}{item.source_name ? ` · ${item.source_name}` : ""}{item.source_date ? ` · ${item.source_date}` : ""}
          </div>
          <p className="text-2 text-sm leading-relaxed mt-1.5 whitespace-pre-wrap">{item.content_md}</p>
          {item.direct_quote && (
            <blockquote className="mt-2 border-l-2 border-cyan-300/60 pl-3 py-1 text-1 text-sm italic" data-testid={`intel-quote-${item.id}`}>
              &ldquo;{item.direct_quote}&rdquo;
              {item.quote_attribution && <div className="not-italic text-3 text-[11px] mt-0.5">— {item.quote_attribution}</div>}
            </blockquote>
          )}
          {(item.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item.tags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/60">#{t}</span>)}
            </div>
          )}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <button onClick={onEdit} data-testid={`intel-edit-${item.id}`} className="text-2 hover:text-cyan-300 p-1"><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={onDelete} data-testid={`intel-delete-${item.id}`} className="text-rose-300/70 hover:text-rose-300 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

function IntelEditDialog({ open, onOpenChange, playerId, intel, onSaved }) {
  const [form, setForm] = useState(intel);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(intel?.id);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.topic.trim() || !form.content_md.trim()) {
      toast.error("Topic and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        topic: form.topic.trim(),
        content_md: form.content_md.trim(),
        direct_quote: form.direct_quote?.trim() || null,
        quote_attribution: form.quote_attribution?.trim() || null,
        source_name: form.source_name?.trim() || null,
        source_role: form.source_role?.trim() || null,
        confidence: form.confidence,
        source_date: form.source_date || null,
        tags: typeof form.tags === "string"
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : (form.tags || []),
        visible_to_family: !!form.visible_to_family,
      };
      if (isEdit) {
        await api.patch(`/intel/${intel.id}`, payload);
        toast.success("Intel updated");
      } else {
        await api.post(`/players/${playerId}/intel`, payload);
        toast.success("Intel added");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="intel-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">{isEdit ? "Edit intel" : "Add intel"}</DialogTitle>
          <DialogDescription className="text-3 text-xs">Source-attributed note about the player. Stays admin-only unless you share it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="intel-field-category" className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  <SelectItem value="on_field">On-Field</SelectItem>
                  <SelectItem value="off_field">Off-Field</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Confidence</Label>
              <Select value={form.confidence} onValueChange={(v) => set("confidence", v)}>
                <SelectTrigger data-testid="intel-field-confidence" className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="single_source">Single Source</SelectItem>
                  <SelectItem value="rumor">Rumor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Topic</Label>
            <Input data-testid="intel-field-topic" value={form.topic} onChange={(e) => set("topic", e.target.value)} placeholder="e.g. Work Ethic, Leadership, Family Dynamics" className="bg-white/5 border-white/10 text-1" />
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Content (markdown supported)</Label>
            <Textarea data-testid="intel-field-content" rows={5} value={form.content_md} onChange={(e) => set("content_md", e.target.value)} placeholder="What you heard, verbatim if possible. No speculation." className="bg-white/5 border-white/10 text-1" />
          </div>
          <div className="rounded-lg border border-cyan-300/20 bg-cyan-300/[0.04] p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Quote className="h-3.5 w-3.5 text-cyan-300" />
              <Label className="text-1 text-xs font-semibold">Verbatim quote (optional)</Label>
              <span className="text-3 text-[10px] ml-auto">AI will reproduce this exactly</span>
            </div>
            <Textarea data-testid="intel-field-direct_quote" rows={2} value={form.direct_quote || ""} onChange={(e) => set("direct_quote", e.target.value)} placeholder='e.g. "Marcus is the first QB I&apos;ve coached who ran his own install meeting at 16."' className="bg-white/5 border-white/10 text-1 text-sm" />
            <Input data-testid="intel-field-quote_attribution" value={form.quote_attribution || ""} onChange={(e) => set("quote_attribution", e.target.value)} placeholder="Quote attribution (e.g. Coach Wallace, HS Head Coach)" className="bg-white/5 border-white/10 text-1 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Source role</Label>
              <Input data-testid="intel-field-source_role" value={form.source_role || ""} onChange={(e) => set("source_role", e.target.value)} placeholder="HS Head Coach, Strength Coach…" className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Source name</Label>
              <Input data-testid="intel-field-source_name" value={form.source_name || ""} onChange={(e) => set("source_name", e.target.value)} placeholder="Coach Derrick Wallace" className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Source date</Label>
              <Input data-testid="intel-field-source_date" type="date" value={form.source_date || ""} onChange={(e) => set("source_date", e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Tags (comma-separated)</Label>
              <Input data-testid="intel-field-tags" value={typeof form.tags === "string" ? form.tags : (form.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value)} placeholder="leadership, adversity" className="bg-white/5 border-white/10 text-1" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-2 text-sm cursor-pointer">
            <input
              data-testid="intel-field-visible"
              type="checkbox"
              checked={!!form.visible_to_family}
              onChange={(e) => set("visible_to_family", e.target.checked)}
              className="accent-cyan-400"
            />
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
            Share this entry with player/family
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="intel-save" onClick={save} disabled={saving} className="cta-primary">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Interview Notes → AI parser. Phase 1: paste raw notes + a source-category
// hint. Phase 2: review/edit the AI-parsed candidate entries before saving.
// ---------------------------------------------------------------------------
function InterviewNotesDialog({ open, onOpenChange, playerId, onSaved }) {
  const [phase, setPhase] = useState("input"); // "input" | "review"
  const [rawNotes, setRawNotes] = useState("");
  const [categoryHint, setCategoryHint] = useState("off_field");
  const [sourceRole, setSourceRole] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [sourceDate, setSourceDate] = useState(new Date().toISOString().slice(0, 10));
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [autoSynth, setAutoSynth] = useState(true);

  const parse = async () => {
    if (!rawNotes.trim()) {
      toast.error("Paste some interview notes first");
      return;
    }
    setParsing(true);
    try {
      const r = await api.post(`/players/${playerId}/intel/parse-notes`, {
        raw_notes: rawNotes,
        category_hint: categoryHint,
        source_role: sourceRole.trim() || null,
        source_name: sourceName.trim() || null,
        source_date: sourceDate || null,
      });
      const parsed = r.data?.entries || [];
      if (parsed.length === 0) {
        toast.error("AI couldn't extract any entries — try adding more detail.");
        return;
      }
      setEntries(parsed.map((e, i) => ({ ...e, _key: `${Date.now()}-${i}` })));
      setPhase("review");
      toast.success(`AI parsed ${parsed.length} ${parsed.length === 1 ? "entry" : "entries"} — review before saving`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI parsing failed");
    } finally {
      setParsing(false);
    }
  };

  const setEntry = (key, patch) =>
    setEntries((arr) => arr.map((e) => (e._key === key ? { ...e, ...patch } : e)));
  const removeEntry = (key) => setEntries((arr) => arr.filter((e) => e._key !== key));

  const saveAll = async () => {
    const valid = entries.filter((e) => e.topic?.trim() && e.content_md?.trim());
    if (valid.length === 0) {
      toast.error("Nothing to save — every entry needs a topic and content");
      return;
    }
    setSaving(true);
    try {
      const items = valid.map((e) => ({
        category: e.category,
        topic: e.topic.trim(),
        content_md: e.content_md.trim(),
        direct_quote: e.direct_quote?.trim() || null,
        quote_attribution: e.quote_attribution?.trim() || null,
        source_name: e.source_name?.trim() || null,
        source_role: e.source_role?.trim() || null,
        confidence: e.confidence || "single_source",
        source_date: e.source_date || null,
        tags: Array.isArray(e.tags) ? e.tags : [],
        visible_to_family: !!e.visible_to_family,
      }));
      await api.post(`/players/${playerId}/intel/bulk`, { items });
      toast.success(`${items.length} intel ${items.length === 1 ? "entry" : "entries"} saved`);
      if (autoSynth) {
        try {
          toast.message("Refreshing Player 360° synthesis…");
          await api.post(`/players/${playerId}/player360/synthesize`);
          toast.success("Player 360° synthesis draft refreshed — review it below");
        } catch {
          toast.error("Saved, but synthesis refresh failed — click Generate Synthesis to retry");
        }
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="interview-notes-dialog">
        <DialogHeader>
          <DialogTitle className="text-1 flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-cyan-300" />
            {phase === "input" ? "Parse Interview Notes" : "Review parsed intel"}
          </DialogTitle>
          <DialogDescription className="text-3 text-xs">
            {phase === "input"
              ? "Paste raw notes from a conversation (parents, coach, teammate). Tag the source as on-field or off-field, then let AI structure it into intel entries."
              : "Review and edit what the AI extracted. Nothing is saved until you click Save. Each entry becomes a Player 360° intel card."}
          </DialogDescription>
        </DialogHeader>

        {phase === "input" ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-2 text-xs">Source category</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="notes-category-toggle">
                {[
                  { v: "on_field", label: "On-Field", icon: Activity, color: "#22D3EE" },
                  { v: "off_field", label: "Off-Field", icon: Home, color: "#60A5FA" },
                  { v: "auto", label: "Auto-detect", icon: Wand2, color: "#A78BFA" },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = categoryHint === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`notes-category-${opt.v}`}
                      onClick={() => setCategoryHint(opt.v)}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border transition ${active ? "bg-white/10 text-1 border-white/30" : "bg-white/[0.03] text-2 border-white/10 hover:border-white/20"}`}
                    >
                      <Icon className="h-3.5 w-3.5" style={{ color: active ? opt.color : undefined }} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-3 text-[11px]">
                {categoryHint === "auto"
                  ? "AI decides each entry's section based on its content."
                  : `All entries default to the ${categoryHint === "on_field" ? "On-Field" : "Off-Field"} section.`}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-2 text-xs">Source role</Label>
                <Input data-testid="notes-source-role" value={sourceRole} onChange={(e) => setSourceRole(e.target.value)} placeholder="Parents, HS Coach…" className="bg-white/5 border-white/10 text-1" />
              </div>
              <div className="space-y-1">
                <Label className="text-2 text-xs">Source name</Label>
                <Input data-testid="notes-source-name" value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="Optional" className="bg-white/5 border-white/10 text-1" />
              </div>
              <div className="space-y-1">
                <Label className="text-2 text-xs">Date</Label>
                <Input data-testid="notes-source-date" type="date" value={sourceDate} onChange={(e) => setSourceDate(e.target.value)} className="bg-white/5 border-white/10 text-1" />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-2 text-xs">Raw interview notes</Label>
              <Textarea
                data-testid="notes-raw-input"
                rows={9}
                value={rawNotes}
                onChange={(e) => setRawNotes(e.target.value)}
                placeholder={'Paste everything you jotted down — bullet points, half-sentences, quotes. e.g.\n"His mom said he\'s been the man of the house since 14. Works two jobs in the offseason. Coach mentioned he runs his own film sessions."'}
                className="bg-white/5 border-white/10 text-1 text-sm"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notes-review-list">
            {entries.length === 0 ? (
              <div className="text-3 text-sm italic py-6 text-center">All entries removed. Go back to re-parse.</div>
            ) : (
              entries.map((e) => (
                <div key={e._key} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5 space-y-2.5" data-testid={`notes-review-entry-${e._key}`}>
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
                    <div className="space-y-1">
                      <Label className="text-2 text-[10px] uppercase tracking-wide">Topic</Label>
                      <Input value={e.topic} onChange={(ev) => setEntry(e._key, { topic: ev.target.value })} className="bg-white/5 border-white/10 text-1 text-sm h-8" data-testid={`notes-entry-topic-${e._key}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-2 text-[10px] uppercase tracking-wide">Section</Label>
                      <Select value={e.category} onValueChange={(v) => setEntry(e._key, { category: v })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-1 h-8 w-[120px] text-xs" data-testid={`notes-entry-category-${e._key}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="glass-strong border-white/10">
                          <SelectItem value="on_field">On-Field</SelectItem>
                          <SelectItem value="off_field">Off-Field</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <button onClick={() => removeEntry(e._key)} className="text-rose-300/70 hover:text-rose-300 p-1.5 mb-1" data-testid={`notes-entry-remove-${e._key}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <Textarea value={e.content_md} rows={2} onChange={(ev) => setEntry(e._key, { content_md: ev.target.value })} className="bg-white/5 border-white/10 text-1 text-sm" data-testid={`notes-entry-content-${e._key}`} />
                  {e.direct_quote ? (
                    <div className="rounded-lg border border-cyan-300/20 bg-cyan-300/[0.04] p-2.5 space-y-1.5">
                      <Textarea value={e.direct_quote} rows={2} onChange={(ev) => setEntry(e._key, { direct_quote: ev.target.value })} className="bg-white/5 border-white/10 text-1 text-sm italic" placeholder="Verbatim quote" data-testid={`notes-entry-quote-${e._key}`} />
                      <Input value={e.quote_attribution || ""} onChange={(ev) => setEntry(e._key, { quote_attribution: ev.target.value })} placeholder="Attribution" className="bg-white/5 border-white/10 text-1 text-xs h-7" />
                    </div>
                  ) : null}
                  <div className="flex items-center gap-3 flex-wrap">
                    <Select value={e.confidence} onValueChange={(v) => setEntry(e._key, { confidence: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-1 h-7 w-[140px] text-xs" data-testid={`notes-entry-confidence-${e._key}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="glass-strong border-white/10">
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="single_source">Single Source</SelectItem>
                        <SelectItem value="rumor">Rumor</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1.5 text-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={!!e.visible_to_family} onChange={(ev) => setEntry(e._key, { visible_to_family: ev.target.checked })} className="accent-cyan-400" data-testid={`notes-entry-visible-${e._key}`} />
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Share with family
                    </label>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter>
          {phase === "input" ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
              <Button data-testid="notes-parse-btn" onClick={parse} disabled={parsing} className="cta-primary">
                <Wand2 className="h-4 w-4" /> {parsing ? "Parsing…" : "Parse with AI"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setPhase("input")} className="text-2">← Back to notes</Button>
              <label className="flex items-center gap-1.5 text-2 text-xs cursor-pointer mr-auto ml-2" data-testid="notes-autosynth-toggle">
                <input type="checkbox" checked={autoSynth} onChange={(e) => setAutoSynth(e.target.checked)} className="accent-cyan-400" />
                <Sparkles className="h-3.5 w-3.5 text-cyan-300" /> Refresh 360° synthesis after saving
              </label>
              <Button data-testid="notes-save-all-btn" onClick={saveAll} disabled={saving || entries.length === 0} className="cta-primary">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : `Save ${entries.length} ${entries.length === 1 ? "entry" : "entries"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
